import React, { useEffect, useState, useContext } from 'react';
import { Row, Col, Card, Typography, Button, Empty, Spin, Table, Tag, message, Avatar, Divider, Skeleton, Radio, DatePicker, Modal } from 'antd';
import {
    IconHeart, IconMapPin, IconFolder, IconEye, IconShare, IconCopy,
    IconUser, IconLayoutDashboard, IconWallet, IconShoppingCart,
    IconMessage, IconGift, IconTrendingUp, IconCalendar, IconPackage
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import UserServ from '../service/UserServ';
import GlobalHeader from '../util/GlobalHeader';
import OrdersServ from '../service/OrdersServ';
import { WishlistContext } from '../WishlistContext';
import Notify from '../util/Notify';
import { usePreference } from '../context/PreferenceContext';
import moment from 'moment';
import EmptyState from '../util/EmptyState';
import "../css/sellerDashboard.css";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const BuyerDashboard = () => {
    const navigate = useNavigate();
    const { wishlistCount } = useContext(WishlistContext);
    const { formatPrice, t } = usePreference();
    const [user, setUser] = useState(null);
    const [stats, setStats] = useState({
        walletBalance: 0,
        credits: 0,
        creditedToday: 0,
        todayOrders: 0,
        totalOrders: 0,
        messagesCount: 0,
        unreadNotifications: 0
    });
    console.log("stats", stats);
    const [recentOrders, setRecentOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 992);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 992);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchUserProfile = async () => {
        try {
            // First try to get user from localStorage for immediate display
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
                try {
                    const userData = JSON.parse(storedUser);
                    setUser(userData);
                } catch (e) {
                    console.error("Error parsing stored user data", e);
                }
            }

            // Then fetch fresh data from API
            const res = await UserServ.getProfile();
            console.log("User profile response:", res);
            if (res?.data) {
                setUser(res.data);
                // Update localStorage with fresh data
                localStorage.setItem('user', JSON.stringify(res.data));
            }
        } catch (error) {
            console.error("Failed to load user profile", error);
        }
    };

    const [period, setPeriod] = useState("all");
    const [customDates, setCustomDates] = useState([]);
    const [isCustomModalVisible, setIsCustomModalVisible] = useState(false);
    const [tempCustomDates, setTempCustomDates] = useState([]);

    const fetchStats = async (showLoading = true) => {
        try {
            if (showLoading && !stats) setLoading(true);

            let params = {};
            if (period !== 'custom') {
                params.timeframe = period;
            } else if (customDates.length === 2) {
                params.timeframe = 'custom';
                params.startDate = customDates[0];
                params.endDate = customDates[1];
            } else {
                setLoading(false);
                return;
            }

            const cacheKey = `cached_buyer_stats_${period}`;
            const cached = localStorage.getItem(cacheKey);
            if (cached && showLoading) {
                try {
                    setStats(JSON.parse(cached));
                    setLoading(false);
                } catch (e) { }
            }

            const res = await UserServ.getBuyerStats(params);
            if (res.data) {
                localStorage.setItem(cacheKey, JSON.stringify(res.data));
                setStats(res.data);
            }
        } catch (error) {
            console.error("Failed to load buyer stats", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchOrders = async (showLoading = true) => {
        try {
            if (showLoading && recentOrders.length === 0) setOrdersLoading(true);
            const cached = localStorage.getItem('cached_buyer_dashboard_orders');
            if (cached && showLoading) {
                try {
                    setRecentOrders(JSON.parse(cached));
                    setOrdersLoading(false);
                } catch (e) { }
            }

            const res = await OrdersServ.getMyOrders({ page: 1, limit: 5 });
            const allOrders = res?.data || (Array.isArray(res) ? res : []);
            // Sort by date desc and take top 5
            const sorted = allOrders.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 5);
            localStorage.setItem('cached_buyer_dashboard_orders', JSON.stringify(sorted));
            setRecentOrders(sorted);
        } catch (error) {
            console.error("Failed to fetch orders", error);
        } finally {
            setOrdersLoading(false);
        }
    };

    useEffect(() => {
        fetchUserProfile();
        fetchOrders();
    }, []);

    useEffect(() => {
        fetchStats();
    }, [period, customDates]);

    const getStatusColor = (status) => {
        const s = status?.toLowerCase();
        const map = {
            ordered: "blue",
            placed: "blue",
            confirmed: "cyan",
            packed: "cyan",
            processing: "orange",
            shipped: "purple",
            delivered: "green",
            completed: "green",
            cancelled: "red",
        };
        return map[s] || "default";
    };

    const getStatusLabel = (status) => {
        const s = status?.toLowerCase();
        const map = {
            ordered: t('ordered'),
            placed: t('ordered'),
            confirmed: t('packed'),
            packed: t('packed'),
            processing: t('processing'),
            shipped: t('shipped'),
            delivered: t('completed'),
            completed: t('completed'),
            cancelled: t('cancelled'),
        };
        return map[s] || status;
    };

    const columns = [
        {
            title: t('order_id'),
            dataIndex: 'orderId',
            key: 'orderId',
            render: (text) => <Text strong style={{ whiteSpace: 'nowrap' }}>{text}</Text>,
        },
        {
            title: t('date'),
            key: 'date',
            render: (_, record) => (
                <Text style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                    {(record.deliveryType === 'Scheduled' || record.deliveryType === 'Custom') && record.scheduledDate ? (
                        <span className="pcu-color-orange pcu-font-600">
                            {moment(record.scheduledDate).format('DD MMM')} {record.scheduledTime}
                        </span>
                    ) : (
                        moment(record.createdAt).format('DD MMM YYYY')
                    )}
                </Text>
            ),
        },
        {
            title: t('amount'),
            key: 'amount',
            render: (_, record) => <Text style={{ whiteSpace: 'nowrap' }}>{formatPrice(record.grandTotal || record.totalOrderAmount || record.totalAmount || 0)}</Text>,
        },
        {
            title: t('status'),
            dataIndex: 'status',
            key: 'status',
            render: (status) => <Tag color={getStatusColor(status)} style={{ whiteSpace: 'nowrap' }}>{getStatusLabel(status)?.toUpperCase()}</Tag>,
        },
        {
            title: t('action'),
            key: 'action',
            render: () => <Button type="link" icon={<IconEye size={16} />} onClick={() => navigate('/buyer/orders')} />
        }
    ];

    // Premium Stat Cards Data
    const statCards = [
        {
            title: t('credits'),
            value: formatPrice(stats.timeframeOverview ? stats.timeframeOverview.credits : stats.credits),
            subValue: `${t('today')}: ${formatPrice(stats.creditedToday)}`,
            icon: <IconWallet size={28} stroke={1.8} />,
            gradient: "linear-gradient(135deg, #6366f1 0%, #a855f7 100%)",
            shadow: "0 8px 24px rgba(99, 102, 241, 0.25)",
            onClick: () => navigate('/buyer/rewards/credits')
        },
        {
            title: t('orders'),
            value: stats.timeframeOverview ? stats.timeframeOverview.orders : stats.todayOrders,
            subValue: `${t('total')}: ${stats.totalOrders}`,
            icon: <IconShoppingCart size={28} stroke={1.8} />,
            gradient: "linear-gradient(135deg, #10b981 0%, #34d399 100%)",
            shadow: "0 8px 24px rgba(16, 185, 129, 0.25)",
            onClick: () => navigate('/buyer/orders')
        },
        {
            title: t('messages'),
            value: stats.unreadNotifications,
            subValue: `${t('total')}: ${stats.messagesCount}`,
            icon: <IconMessage size={28} stroke={1.8} />,
            gradient: "linear-gradient(135deg, #f43f5e 0%, #fb7185 100%)",
            shadow: "0 8px 24px rgba(244, 63, 94, 0.25)",
        },
        {
            title: t('wishlist'),
            value: wishlistCount,
            subValue: t('saved_items'),
            icon: <IconHeart size={28} stroke={1.8} />,
            gradient: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)",
            shadow: "0 8px 24px rgba(245, 158, 11, 0.25)",
            onClick: () => navigate('/buyer/wishlist')
        }
    ];

    return (
        <div className="sd-container" style={{ padding: '24px' }}>
            {/* Header Section */}
            <div className="sd-header" style={{ marginBottom: '32px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{
                        width: '56px',
                        height: '56px',
                        background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                        borderRadius: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 8px 16px rgba(99, 102, 241, 0.2)'
                    }}>
                        <IconUser size={28} color="white" />
                    </div>
                    <div>
                        <h1 className="sd-title">
                            {user ? `${user.firstName} ${user.lastName}` : t('buyer')}
                        </h1>
                        <p className="sd-subtitle">{t('Welcome back')}! {t('heres your overview')}</p>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <div className="sd-premium-filters" style={{ margin: 0, padding: 0, background: 'transparent' }}>
                        <Radio.Group className="sd-premium-radio-group" value={period} onChange={e => {
                            if (e.target.value !== 'custom') {
                                setPeriod(e.target.value);
                            }
                        }}>
                            <Radio.Button value="all">All</Radio.Button>
                            <Radio.Button value="today">Today</Radio.Button>
                            <Radio.Button value="last_7_days">Week</Radio.Button>
                            <Radio.Button value="this_month">Month</Radio.Button>
                            <Radio.Button value="custom" onClick={() => {
                                setIsCustomModalVisible(true);
                                setTempCustomDates(customDates);
                            }}>Custom</Radio.Button>
                        </Radio.Group>
                    </div>
                    <Button
                        icon={<IconMapPin size={18} />}
                        onClick={() => navigate('/buyer/addresses')}
                        style={{ height: '44px', borderRadius: '12px', fontWeight: 600 }}
                    >
                        {t('address')}
                    </Button>
                    <div className="sd-header-badge">
                        <IconTrendingUp size={14} />
                        <span>{t('live_data')}</span>
                    </div>
                </div>
            </div>

            {/* Stats Row */}
            <div className="sd-main-row" style={{ marginBottom: '32px' }}>
                <div className="sd-stats-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gridTemplateRows: 'unset', flex: '1' }}>
                    {statCards.map((item, index) => (
                        <div key={index} className="sd-stat-card" style={{ background: item.gradient, boxShadow: item.shadow }} onClick={item.onClick}>
                            <Skeleton loading={loading} active paragraph={{ rows: 1 }}>
                                <div className="sd-stat-icon" style={{ background: 'rgba(255,255,255,0.2)' }}>
                                    {React.cloneElement(item.icon, { color: "#fff" })}
                                </div>
                                <div className="sd-stat-info">
                                    <span className="sd-stat-label">{item.title}</span>
                                    <span className="sd-stat-value" style={{ fontSize: '20px' }}>{item.value}</span>
                                    <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>{item.subValue}</span>
                                </div>
                            </Skeleton>
                        </div>
                    ))}
                </div>
            </div>

            <Row gutter={[24, 24]}>
                {/* Left Column: Latest Orders */}
                <Col xs={24} lg={16}>
                    <div className="main-content-card pcu-bg-white pcu-radius-24 pcu-shadow-sm pcu-overflow-hidden" style={{ border: '1px solid #f1f5f9' }}>
                        <div className="pcu-padding-x-24 pcu-padding-top-24 pcu-padding-bottom-16 pcu-flex pcu-justify-between pcu-items-center">
                            <h3 style={{ margin: 0, fontWeight: 800, color: '#1e293b', fontSize: '18px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <IconPackage size={20} color="#6366f1" />
                                {t('latest_orders')}
                            </h3>
                            <Button type="link" onClick={() => navigate('/buyer/orders')} style={{ fontWeight: 700 }}>
                                {t('view_all')}
                            </Button>
                        </div>

                        <div className="pcu-padding-x-24 pcu-padding-bottom-24">
                            {recentOrders.length > 0 ? (
                                <Table
                                    columns={columns}
                                    dataSource={recentOrders}
                                    rowKey="_id"
                                    pagination={false}
                                    loading={ordersLoading}
                                    className="premium-table"
                                />
                            ) : (
                                <div className="pcu-min-h-300 pcu-flex-column pcu-items-center pcu-flex-center">
                                    <EmptyState
                                        title="No Order History"
                                        description={t('no_record_found')}
                                        gradient="purple"
                                        showAction={false}
                                        icon="inbox"
                                        size="small"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </Col>

                {/* Right Column: Share & Earn */}
                <Col xs={24} lg={8}>
                    <div className="share-earn-card" style={{
                        background: 'radial-gradient(circle at top right, #4338ca 0%, #1e1b4b 100%)',
                        borderRadius: '24px',
                        padding: '32px',
                        color: '#fff',
                        height: '100%',
                        boxShadow: '0 20px 40px rgba(30, 27, 75, 0.2), inset 0 0 40px rgba(99, 102, 241, 0.1)',
                        position: 'relative',
                        overflow: 'hidden',
                        border: '1px solid rgba(255,255,255,0.1)'
                    }}>
                        {/* Radiant Glow Elements */}
                        <div style={{ position: 'absolute', top: '-20%', right: '-20%', width: '200px', height: '200px', background: 'radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%)', borderRadius: '50%' }}></div>

                        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#fff', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <IconGift size={22} color="#fff" /> {t('share_earn')}
                        </h3>

                        <div className="pcu-flex-column pcu-items-center pcu-text-center">
                            <div style={{
                                width: '80px',
                                height: '80px',
                                background: 'rgba(255,255,255,0.05)',
                                borderRadius: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: '20px',
                                border: '1px solid rgba(255,255,255,0.1)',
                                backdropFilter: 'blur(10px)'
                            }}>
                                <IconShare size={40} color="#fff" />
                            </div>

                            <Title level={4} style={{ color: '#fff', marginBottom: '12px', fontWeight: 800 }}>{t('invite_friends')}</Title>
                            <Text style={{ color: 'rgba(255,255,255,0.85)', marginBottom: '24px', fontSize: '13px', lineHeight: 1.6 }}>
                                {t('share_referral_text')}
                            </Text>

                            <div style={{
                                background: 'rgba(255,255,255,0.05)',
                                borderRadius: '16px',
                                padding: '8px',
                                width: '100%',
                                border: '1px solid rgba(255,255,255,0.1)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '8px'
                            }}>
                                <div style={{
                                    padding: '10px 16px',
                                    background: 'rgba(255,255,255,0.1)',
                                    borderRadius: '12px',
                                    fontWeight: 800,
                                    fontSize: '16px',
                                    color: '#fff',
                                    letterSpacing: '1px',
                                    flex: 1
                                }}>
                                    {user?.userId || '...'}
                                </div>
                                <Button
                                    type="primary"
                                    icon={<IconCopy size={18} />}
                                    style={{
                                        height: '44px',
                                        borderRadius: '12px',
                                        background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                                        border: 'none',
                                        fontWeight: 700
                                    }}
                                    onClick={() => {
                                        const referralCode = user?.userId;
                                        if (referralCode) {
                                            navigator.clipboard.writeText(referralCode)
                                                .then(() => Notify.success("Referral code copied!"))
                                                .catch(() => Notify.error("Failed to copy code"));
                                        }
                                    }}
                                >
                                    {t('copy')}
                                </Button>
                            </div>

                            <Button
                                block
                                type="text"
                                icon={<IconShare size={18} color="#fff" />}
                                style={{ color: '#fff', marginTop: '16px', fontWeight: 600, opacity: 0.9 }}
                                onClick={() => {
                                    const referralCode = user?.userId;
                                    const shareData = {
                                        title: 'Nearzon Referral',
                                        text: `🔥 Hey! Check this out 👇\n\nI've been using *Nearzon* for awesome deals 🛍️\nYou should try it too!\n\n🎁 Use my referral code: *${referralCode}*\nand unlock exclusive benefits\n\n📲 Download now: https://nearzon.com`
                                    };
                                    if (navigator.share) {
                                        navigator.share(shareData).catch(console.error);
                                    }
                                }}
                            >
                                {t('share_link')}
                            </Button>
                        </div>
                    </div>
                </Col>
            </Row>

            <style dangerouslySetInnerHTML={{
                __html: `
                .premium-pagination {
                    margin: 24px 0 0 0 !important;
                }
                .share-earn-card h3, 
                .share-earn-card h4, 
                .share-earn-card .ant-typography,
                .share-earn-card .ant-btn-text {
                    color: #fff !important;
                }
                .share-earn-card svg {
                    color: #fff !important;
                }
                .ant-radio-checked .ant-radio-inner {
                }
                .premium-table .ant-table {
                    background: transparent !important;
                }
                .premium-table .ant-table-thead > tr > th {
                    background: #f8fafc !important;
                    color: #64748b !important;
                    font-weight: 700 !important;
                    text-transform: uppercase !important;
                    font-size: 11px !important;
                    letter-spacing: 0.5px !important;
                    padding: 16px 20px !important;
                    border-bottom: 2px solid #f1f5f9 !important;
                    white-space: nowrap;
                }
                .premium-table .ant-table-tbody > tr > td {
                    padding: 16px 20px !important;
                    border-bottom: 1px solid #f1f5f9 !important;
                    color: #1e293b !important;
                    font-weight: 500 !important;
                }
                .premium-table .ant-table-tbody > tr:hover > td {
                    background: #fdfdfd !important;
                }
                @media (max-width: 992px) {
                    .sd-stats-grid {
                        grid-template-columns: 1fr 1fr !important;
                    }
                }
                @media (max-width: 576px) {
                    .sd-stats-grid {
                        grid-template-columns: 1fr !important;
                    }
                }
            `}} />
            <Modal
                title="Select Custom Date Range"
                open={isCustomModalVisible}
                onOk={() => {
                    if (tempCustomDates && tempCustomDates.length === 2) {
                        setCustomDates(tempCustomDates);
                        setPeriod('custom');
                        setIsCustomModalVisible(false);
                    }
                }}
                onCancel={() => setIsCustomModalVisible(false)}
                okText="Apply Filter"
                cancelText="Cancel"
                okButtonProps={{ disabled: !tempCustomDates || tempCustomDates.length !== 2 }}
                width={400}
                style={{ top: 40 }}
            >
                <div style={{ paddingTop: '24px', paddingBottom: '24px', display: 'flex', justifyContent: 'center' }}>
                    <RangePicker
                        className="sd-premium-datepicker"
                        style={{ width: '100%' }}
                        onChange={(dates, dateStrings) => {
                            if (dateStrings[0] && dateStrings[1]) {
                                setTempCustomDates([new Date(dateStrings[0]).toISOString(), new Date(dateStrings[1]).toISOString()]);
                            } else {
                                setTempCustomDates([]);
                            }
                        }}
                    />
                </div>
            </Modal>
        </div>
    );
};

export default BuyerDashboard;
